import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, Typography, Paper, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Button, TextField, 
  Dialog, DialogActions, DialogContent, DialogTitle, 
  IconButton, Box, Alert, Snackbar, TablePagination,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
// eslint-disable-next-line
import { Add as AddIcon, Search as SearchIcon, Visibility as VisibilityIcon, Edit as EditIcon, Delete as DeleteIcon, HourglassTop as HourglassTopIcon, SaveAs as SaveAsIcon, Close as CloseIcon } from '@mui/icons-material';
import Header from "../../components/common/header/Header";
import Footer from "../../components/common/footer/Footer";
// eslint-disable-next-line
import { getApiCall, postApiCall, putApiCall, deleteApiCall } from '../../utils/Apicalls';
import './AdminPage.css';

function AdminPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [userDetail, setUserDetail] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openMinutesHistoryDialog, setOpenMinutesHistoryDialog] = useState(false);
  const [openMinutesDialog, setOpenMinutesDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    mobile: '',
    role_id: 'user'
  });

  const [minuteListLoading, setMinuteListLoading] = useState(false);
  const [usedMinutes, setUsedMinutes] = useState(0);
  const [minuteList, setMinuteList] = useState([]);
  const [minuteListType, setMinuteListType] = useState("webApp");
  const [minutesToAdd, setMinutesToAdd] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [roleList, setRoleList] = useState([]);

  // Check if user is admin and fetch users
  useEffect(() => {
    const checkAdminAndFetchUsers = async () => {
      const userDetails = localStorage.getItem("userDetails");
      if (!userDetails) {
        navigate('/login');
        return;
      }

      try {
        const userData = JSON.parse(userDetails);
        if (userData) {
          setUserDetail(userData);
        }
        if (['admin', 'sales', 'tennant'].indexOf(userData.role_id) === -1) {
          navigate('/home');
          return;
        } 
        if (['tennant'].indexOf(userData.role_id) > -1) {
          navigate('/tennant-users/' + userData.id);
          return;
        } 

        switch (userData.role_id) {
          case 'admin':
            setRoleList(['admin', 'sales', 'tennant', 'user']);
            break;
          case 'sales':
            setRoleList(['sales', 'tennant', 'user']);
            break;
          default:
            setRoleList(['user']);
            break;
        }

        fetchUsers({
          page: 0,
          limit: rowsPerPage
        }, userData);
      } catch (error) {
        navigate('/login');
      }
    };

    checkAdminAndFetchUsers();
  }, [navigate]);

  const fetchUsers = async (pageConfig, userData) => {
    setLoading(true);
    setTotalUsers(0);
    setUsers([]);
    setError('');
    try {
      let query = `page=${pageConfig.page + 1}&limit=${pageConfig.limit}`;
      switch (userData.role_id) {
        case 'admin':
          query += `&roles=admin,sales,tennant,user,init&parentUserId=`;
          break;
        case 'sales':
          query += `&roles=sales,tennant,user,init&parentUserId=`;
          break;
        case 'tennant':
          query += `&roles=b2b&parentUserId=`+userData.id;
          break;
        default:
          query += `&roles=user`;
          break;
      }
      if (searchQuery) {
        query += `&search=${searchQuery}`;
      }
      const result = await getApiCall('interview-app/users/list?' + query);
      if (result?.status) {
        if (result?.result?.length > 0) {
          setUsers(result?.result);
        }
        setTotalUsers(parseInt(result?.totalRecords || 0));
      } else {
        setError('Failed to fetch users');
      }
    } catch (err) {
      setError('An error occurred while fetching users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        email: user.email || '',
        mobile: user.mobile || '',
        role_id: user.role_id || 'user'
      });
      setCurrentUser(user);
    } else {
      setFormData({
        firstName: '',
        email: '',
        mobile: '',
        role_id: 'user'
      });
      setCurrentUser(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenMinutesHistoryDialog = async (user) => {
    setCurrentUser(user);
    setMinutesToAdd('');
    setMinuteList([]);
    setMinuteListType("webApp");
    setMinuteListLoading(true);
    setOpenMinutesHistoryDialog(true); 
    getMinutesHistory(user, "webApp");
  };

  const getMinutesHistory = async (user, type) => {
    try {
      const result = await getApiCall(`interview-app/interview/history/${user.id}/${type}`);
      if (result?.status) {
        if (result?.result?.list?.length > 0) {
          setMinuteList(result.result.list.map((m) => {
            m.editable = false;
            return m;
          }));
        }
        setUsedMinutes(result?.result?.usedMinutes || 0);
      } else {
        setError('Failed to fetch minutes list');
      }
    } catch (err) {
      setError('An error occurred while fetching minutes list');
    } finally {
      setMinuteListLoading(false);
    }
  };

  const handleCloseMinutesDialog = () => {
    setOpenMinutesDialog(false);
  };

  const handleOpenMinutesDialog = () => {
    setMinutesToAdd(0);
    setOpenMinutesDialog(true);
  };

  const handleCloseMinutesHistoryDialog = () => {
    setOpenMinutesHistoryDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const saveMinutesUpdate = async (minuteHistory) => {
    let purchase_minutes = parseInt(minuteHistory.purchase_minutes);

    if (!(purchase_minutes >= 0 && purchase_minutes >= minuteHistory.remainingMinutes)) {
      setSnackbar({
        open: true,
        message: `Please enter a valid number of minutes, but not more than remaining minutes(${minuteHistory.remainingMinutes}) available`,
        severity: 'error'
      });
      return;
    }
    let url = 'interview-app/purchaseplan/updateminutes/' + minuteHistory.id;
    let paramBody = {
      minutes: purchase_minutes
    };

    console.log('minuteHistory:', minuteHistory);

    let result = await postApiCall(url, paramBody);
    console.log('result:', result);
    if (result?.status) {
      setSnackbar({
        open: true,
        message: 'Minutes updated successfully',
        severity: 'success'
      });
      minuteHistory.editable = false;
      setMinuteList([...minuteList]);
      fetchUsers({
        page: 0,
        limit: rowsPerPage
      }, userDetail);
    }
  };

  const handleSubmit = async () => {
    try {
      let result;
      let url = 'interview-app/users/add';
      let paramBody = {
        firstName: formData.firstName,
        lastName: "",
        email: formData.email,
        mobile: formData.mobile,
        minutes: 0
      };

      if (userDetail?.role_id === "tennant") {
        url = `interview-app/users/addtennantuser/${userDetail.id}`;
      } else if (formData.role_id === "tennant") {
        paramBody['roleType'] = formData.role_id;
      }
      
      if (currentUser) {
        // Update existing user
        // result = await putApiCall(`interview-app/users/${currentUser._id}`, formData);
      } else {
        result = await postApiCall(url, paramBody);
      }
      
      if (result?.status) {
        setSnackbar({
          open: true,
          message: currentUser ? 'User updated successfully' : 'User created successfully',
          severity: 'success'
        });
        fetchUsers({
          page: 0,
          limit: rowsPerPage
        }, userDetail);
        handleCloseDialog();
      } else {
        setSnackbar({
          open: true,
          message: result?.message || 'Operation failed',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'An error occurred',
        severity: 'error'
      });
    }
  };

  const handleAddMinutes = async () => {
    if (!currentUser || !minutesToAdd || isNaN(minutesToAdd) || parseInt(minutesToAdd) <= 0) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid number of minutes',
        severity: 'error'
      });
      return;
    }

    try {
      const result = await postApiCall(`interview-app/purchaseplan/addminutes`, {
        minutes: parseInt(minutesToAdd),
        type: minuteListType,
        userId: currentUser.id
      });
      
      if (result?.status) {
        setSnackbar({
          open: true,
          message: `Successfully added ${minutesToAdd} minutes to ${currentUser.first_name}`,
          severity: 'success'
        });
        getMinutesHistory(currentUser, minuteListType);
        fetchUsers({
          page: 0,
          limit: rowsPerPage
        }, userDetail);
        handleCloseMinutesDialog();
      } else {
        setSnackbar({
          open: true,
          message: result?.message || 'Failed to add minutes',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'An error occurred while adding minutes',
        severity: 'error'
      });
    }
  };

  // eslint-disable-next-line
  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const result = await deleteApiCall(`interview-app/admin/users/${userId}`);
        
        if (result?.status) {
          setSnackbar({
            open: true,
            message: 'User deleted successfully',
            severity: 'success'
          });
          fetchUsers({
            page: 0,
            limit: rowsPerPage
          }, userDetail);
        } else {
          setSnackbar({
            open: true,
            message: result?.message || 'Failed to delete user',
            severity: 'error'
          });
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'An error occurred while deleting user',
          severity: 'error'
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchUsers({
      page: newPage,
      limit: rowsPerPage
    }, userDetail);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
    fetchUsers({
      page: 0,
      limit: event.target.value
    }, userDetail);
  };

  const searchUser = () => {
    setPage(0);
    fetchUsers({
      page: 0,
      limit: rowsPerPage
    }, userDetail);
  }

  return (
    <div className="admin-page">
      <Header />
      <Container className="admin-container">
        <Box className="admin-header">
          <Typography variant="h4" component="h1">
            Admin Dashboard
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add User
          </Button>
        </Box>
        <div className="row">
          <div className="col-md-4">
            <TextField
              label=""
              placeholder="Search by Name or Email"
              variant="outlined"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2, backgroundColor: "#ffffff" }}
            />
          </div>
          <div className="col-4">
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<SearchIcon />}
              onClick={() => searchUser()}
            >
              Search
            </Button>
          </div>
        </div>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper className="users-table-container">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Remaining Minutes</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  users
                    .map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.first_name} {user.last_name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.mobile || '-'}</TableCell>
                        <TableCell >{user.role_id}</TableCell>
                        <TableCell>
                          <div className="row">
                            <div className="col-12">
                              <p><strong>Web:</strong> {user.remainingMinutes} {user.remainingMinutes > 1 ? 'minutes' : 'minute'}</p>
                            </div>
                            <div className="col-12">
                              <p><strong>Desktop:</strong> {user.remainingDesktopMinutes} {user.remainingDesktopMinutes > 1 ? 'minutes' : 'minute'}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Box className="action-buttons">
                            <IconButton 
                              color="primary" 
                              onClick={() => handleOpenMinutesHistoryDialog(user)}
                              title="Manage Minutes"
                            >
                              <HourglassTopIcon />
                            </IconButton>
                            {user.role_id === 'tennant' && (
                              <IconButton 
                                color="primary" 
                                onClick={() => navigate('/tennant-users/' + user.id)}
                                title="View tennant users"
                              >
                                <VisibilityIcon />
                              </IconButton>
                            )}
                            {/* <IconButton 
                              color="secondary" 
                              onClick={() => handleOpenDialog(user)}
                              title="Edit User"
                            >
                              <EditIcon />
                            </IconButton> */}
                            {/* <IconButton 
                              color="error" 
                              onClick={() => handleDeleteUser(user._id)}
                              title="Delete User"
                            >
                              <DeleteIcon />
                            </IconButton> */}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalUsers}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Container>

      {/* User Form Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <div className='row'>
            <div className='col-7'>
              {currentUser ? 'Edit User' : 'Add New User'}
            </div>
            <div className='col-5 text-end'>
              <button 
                className="exit-button" 
                onClick={handleCloseDialog} 
                title="Exit"
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="firstName"
            label="Name"
            type="text"
            fullWidth
            value={formData.firstName}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="email"
            label="Email"
            type="email"
            fullWidth
            value={formData.email}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="mobile"
            label="Mobile"
            type="text"
            fullWidth
            value={formData.mobile}
            onChange={handleInputChange}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel id="role-select-label">Role</InputLabel>
            <Select
              labelId="role-select-label"
              name="role_id"
              value={formData.role_id}
              label="Role"
              onChange={handleInputChange}
            >
              {roleList.map((role, key) => (
                <MenuItem key={key} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {currentUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* minutes history Dialog */}
      <Dialog open={openMinutesHistoryDialog} onClose={handleCloseMinutesHistoryDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <div className='row'>
            <div className='col-6'>Manage Minutes</div>
            <div className='col-5 text-end'>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<AddIcon />}
                onClick={() => handleOpenMinutesDialog()}
                styles={{marginRight: 10, right: 50}}
              >
                Add Minutes
              </Button>
            </div>
            <div className='col-1 text-end'>
              <button 
                className="exit-button" 
                onClick={handleCloseMinutesHistoryDialog} 
                title="Exit"
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className='row mb-3'>
            <div className='col-3'>
              <FormControl fullWidth margin="dense" size="small">
                <InputLabel id="type-select-label">Type</InputLabel>
                <Select
                  labelId="type-select-label"
                  name="type_id"
                  value={minuteListType}
                  label="Role"
                  onChange={(e) => {
                    console.log(e.target.value);
                    setMinuteListType(e.target.value);
                    setMinuteList([]);
                    setMinuteListLoading(true);
                    getMinutesHistory(currentUser, e.target.value);
                  }}
                >
                  <MenuItem value="webApp">Web App</MenuItem>
                  <MenuItem value="desktopApp">Desktop App</MenuItem>
                </Select>
              </FormControl>
            </div>
          </div>
          <Paper className="users-table-container">
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Plan Name</TableCell>
                    <TableCell>Purchased Minutes</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {minuteListLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : minuteList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No history found
                      </TableCell>
                    </TableRow>
                  ) : (
                    minuteList
                      .map((minuteHistory) => (
                        <TableRow key={minuteHistory.id}>
                          <TableCell>{minuteHistory.plan_name}</TableCell>
                          <TableCell>
                            <TextField
                              margin="dense"
                              name="minutes"
                              label="Minutes"
                              type="number"
                              disabled={minuteHistory.editable == false}
                              fullWidth
                              value={minuteHistory.purchase_minutes}
                              onChange={(e) => {
                                console.log(e.target.value);
                                minuteHistory.purchase_minutes = e.target.value;
                                setMinuteList([...minuteList]);
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box className="action-buttons">
                              {minuteHistory?.isRemainingMinutes == true && (
                                <>
                                  {minuteHistory.editable == true ? (
                                    <IconButton 
                                      color="primary" 
                                      onClick={() => saveMinutesUpdate(minuteHistory)}
                                      title="Save Minutes"
                                    >
                                      <SaveAsIcon />
                                    </IconButton>
                                  ) : (
                                    <IconButton 
                                      color="primary" 
                                      onClick={() => {
                                        minuteHistory.editable = true;
                                        setMinuteList([...minuteList]);
                                        console.log(minuteHistory);
                                      }}
                                      title="Edit Minutes"
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  )}
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </DialogContent>
      </Dialog>

      {/* Add minutes Dialog */}
      <Dialog open={openMinutesDialog} onClose={handleCloseMinutesDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          <div className='row'>
            <div className='col-7'>Add Minutes</div>
            <div className='col-5 text-end'>
              <button 
                className="exit-button" 
                onClick={handleCloseMinutesDialog} 
                title="Exit"
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Adding Minutes for: {currentUser?.firstName}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Minutes to Add"
            type="number"
            fullWidth
            value={minutesToAdd}
            onChange={(e) => setMinutesToAdd(e.target.value)}
            inputProps={{ min: "1", step: "1" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMinutesDialog}>Cancel</Button>
          <Button onClick={handleAddMinutes} variant="contained" color="primary">
            Add Minutes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default AdminPage;
